/* ==========================================================================
   Home -- find a cleaner
   Browsing is always open. A cleaner's free hours come from their calendar,
   so nobody has to "post" availability. Filtering by date is what makes this
   usable without posting a request at all.
   ========================================================================== */

import type { Cleaner, House, MatchResult, Request } from '../../types';
import { Icon } from '../../components/Icon';
import { PullToRefresh } from '../../components/PullToRefresh';
import {
  Avatar, Card, Empty, JobChips, MatchBar, Pick, SectionHead, Stars, matches,
} from '../../components/ui';
import { CLEANERS, REQUESTS, AVAILABILITY } from '../../data/seed';
import {
  areaLabel, byId, dayState, fitFor, freeNow, freeOn, lang, milesChip,
} from '../../lib/domain';
import { TODAY_ISO, addDays, fmtDate, fmtTime, money, nowSlot } from '../../lib/time';
import { t } from '../../lib/i18n';
import {
  activeHouse, blankPeopleFilter, clearCtx, commit, inviteToRequest, nav, openSheet, refresh,
  state, tab,
} from '../../store';

export const PEOPLE_SORTS = [
  { id: 'match', key: 'bestmatch' },
  { id: 'cheap', key: 'cheapest' },
  { id: 'dear', key: 'dearest' },
  { id: 'rated', key: 'bestrated' },
] as const;

export function FindCleaner() {
  const f = state.filters.people;
  const h = activeHouse();
  const ctxReq = state.ctx ? byId(REQUESTS, state.ctx) : undefined;

  const list = CLEANERS.filter((c) => {
    const av = AVAILABILITY[c.id];
    if (av && av.hidden) return false;
    /* A pick is either a town ("Lakewood, NJ") or one of the old
       twenty-six area tags. Match on both: the picker hands out towns now,
       but every account that existed before it shipped carries area tags,
       and a filter that understood only one of them would silently return
       nobody. A cleaner's town is where they are based -- how far they will
       travel is their radius, which the match score already weighs. */
    if (f.areas.length && !f.areas.some((a) => c.areas.includes(a) || c.town === a)) return false;
    if (f.tags.length && !f.tags.every((x) => c.specialties.includes(x) || c.traits.includes(x))) return false;
    if (f.langs.length && !f.langs.some((l) => c.languages.some((cl) => cl.id === l))) return false;
    if (c.rate < f.rateMin || c.rate > f.rateMax) return false;
    if (f.gender !== 'any' && c.gender !== f.gender) return false;
    if (f.minRating && c.ratings.overall < f.minRating) return false;
    /* "Now" is a stricter question than "today": free from the next quarter
       hour, not free at some point before bedtime. */
    if (f.now && !freeNow(c.id, ctxReq ? ctxReq.hours : 2)) return false;
    if (!f.now && f.date && !freeOn(c.id, f.date, ctxReq ? ctxReq.hours : 3)) return false;
    if (f.q && !matches(c.name + ' ' + c.headline, f.q)) return false;
    return true;
  }).map((c) => ({
    c,
    m: fitFor(c, h, ctxReq
      ? { jobIds: ctxReq.jobIds, rate: ctxReq.rate, hours: ctxReq.hours }
      : {}),
  }));

  const sorters: Record<string, (a: { c: Cleaner; m: MatchResult }, b: { c: Cleaner; m: MatchResult }) => number> = {
    match: (a, b) => b.m.score - a.m.score,
    cheap: (a, b) => a.c.rate - b.c.rate,
    dear: (a, b) => b.c.rate - a.c.rate,
    rated: (a, b) => b.c.ratings.overall - a.c.ratings.overall,
  };
  list.sort(sorters[f.sort] || sorters.match);

  const activeCount =
    f.areas.length + f.tags.length + f.langs.length +
    (f.gender !== 'any' ? 1 : 0) + (f.minRating ? 1 : 0) + (f.date || f.now ? 1 : 0) +
    (f.rateMin > 15 || f.rateMax < 200 ? 1 : 0) + (f.q ? 1 : 0);

  const setDate = (d: string) => { f.date = f.date === d ? '' : d; f.now = false; commit(); };
  const custom = f.date && f.date !== TODAY_ISO && f.date !== addDays(TODAY_ISO, 1);

  return (
    <>
      <div className="filterbar">
        <Pick
          on={activeCount > 0}
          onClick={() => openSheet({ name: 'peoplefilter' })}
          label={<><Icon name="filter" /> {t('filters')}{activeCount ? ' · ' + activeCount : ''}</>}
        />
        {/* Not another date chip: "today" is a day, "now" is the next
            couple of hours, and somebody who needs help before the guests
            arrive means the second one. It pins the date to today and asks
            who is free from the next quarter hour. */}
        <Pick
          label={t('now')}
          on={f.now}
          onClick={() => {
            f.now = !f.now;
            f.date = f.now ? nowSlot().date : '';
            commit();
          }}
        />
        <Pick
          label={t('today')}
          on={f.date === TODAY_ISO && !f.now}
          onClick={() => { f.now = false; setDate(TODAY_ISO); }}
        />
        <Pick
          label={t('tomorrow')}
          on={f.date === addDays(TODAY_ISO, 1)}
          onClick={() => setDate(addDays(TODAY_ISO, 1))}
        />
        <Pick
          on={!!custom}
          onClick={() => openSheet({ name: 'pickdate' })}
          label={<><Icon name="cal" /> {custom ? fmtDate(f.date) : t('pickdate')}</>}
        />
        <Pick
          onClick={() => openSheet({ name: 'peoplesort' })}
          label={<><Icon name="sort" /> {t(PEOPLE_SORTS.find((s) => s.id === f.sort)!.key)}</>}
        />
      </div>

      <PullToRefresh onRefresh={refresh}>
      <div className="screen-pad">
        {ctxReq ? (
          <div className="context">
            <div className="col grow">
              <b>Searching for your {fmtDate(ctxReq.date, { long: true })} request</b>
              <span>
                {ctxReq.hours}h · {money(ctxReq.rate)}{t('perhour')} · {ctxReq.jobIds.length} jobs ·
                filtered to who is actually available
              </span>
            </div>
            <button type="button" onClick={clearCtx}>Clear</button>
          </div>
        ) : (
          <Card className="warm prompt">
            <b>{t('f_nobody')}</b>
            <span className="sub">
              {t('f_postinstead')}
            </span>
            <button type="button" className="btn warm block" onClick={() => tab('h_post')}>
              {t('f_postneed')}
            </button>
          </Card>
        )}

        <SectionHead
          title={
            list.length + ' ' + t('cleaners_n')
            + (f.now
              ? ' ' + t('freefrom') + ' ' + fmtTime(nowSlot().start)
              : f.date ? ' ' + t('availableon') + ' ' + fmtDate(f.date) : '')
          }
        />

        {list.length === 0 && (
          <Empty
            big={t('f_nomatchall')}
            sub={t('f_strict')}
            action={t('b_clear')}
            onAction={() => { state.filters.people = blankPeopleFilter(); commit(); }}
          />
        )}

        {/* A column on a phone, a grid on a desk. Browsing people is the one
            screen where seeing several at once is the whole point. */}
        <div className="cardgrid">
          {list.map(({ c, m }) => (
            <CleanerCard key={c.id} c={c} h={h} m={m} ctxReq={ctxReq} />
          ))}
        </div>
      </div>
      </PullToRefresh>
    </>
  );
}

function CleanerCard({
  c, h, m, ctxReq,
}: { c: Cleaner; h: House; m: MatchResult; ctxReq?: Request }) {
  const f = state.filters.people;
  const st = f.date ? dayState(c.id, f.date) : null;
  const shared = c.languages.filter((cl) => h.languages.some((hl) => hl.id === cl.id));

  return (
    <Card onClick={() => nav('h_cleaner', { id: c.id })}>
      <div className="row top">
        <Avatar p={c} />
        <div className="col grow">
          <div className="row between">
            <b className="name">{c.name}</b>
            <span className="rate">{money(c.rate)}<small>{t('perhour')}</small></span>
          </div>
          <span className="sub">{c.headline}</span>
          <div className="row" style={{ gap: 6, marginTop: 3 }}>
            <Stars value={c.ratings.overall} />
            <span className="sub tight">
              {c.ratings.count} {t('reviews')} · {c.yearsExp}y ·{' '}
              {c.gender === 'female' ? t('woman') : t('man')}
            </span>
          </div>
        </div>
      </div>

      {st && st.windows.length > 0 && (
        <div className="freewin">
          <Icon name="clock" /> {t('available')}{' '}
          {st.windows.map((w) => fmtTime(w.from) + '–' + fmtTime(w.to)).join(', ')}
          {st.status === 'partial' ? ' (' + t('partofday') + ')' : ''}
        </div>
      )}

      <div className="chips">
        {c.areas.includes(h.area) ? (
          <span className="chip good">{t('covers')} {areaLabel(h.area)}</span>
        ) : c.milesAway !== null ? (
          <span className="chip warnc">{milesChip(c.milesAway)}</span>
        ) : null}
        {shared.length ? (
          <span className="chip tile">{t('bothspeak')} {lang(shared[0].id)}</span>
        ) : (
          <span className="chip warnc">{t('f_nolang')}</span>
        )}
        {c.team && <span className="chip warm">Team of {c.team}</span>}
      </div>

      <div className="chips">
        <JobChips
          ids={ctxReq ? ctxReq.jobIds.filter((j) => c.specialties.includes(j)) : c.specialties}
          max={3}
        />
      </div>

      <div className="divider" />
      <div className="col" style={{ gap: 6 }}>
        <div className="row between">
          <span className="sub tight" style={{ fontWeight: 700, color: 'var(--tile)' }}>
            {m.score}% {t('match')}
          </span>
          <span className="sub tight">{m.reasons[0] || ''}</span>
        </div>
        <MatchBar score={m.score} />
      </div>

      {ctxReq && (
        <button
          type="button"
          className="btn sm block"
          onClick={(e) => { e.stopPropagation(); inviteToRequest(ctxReq.id, c.id); }}
        >
          {t('f_invite')}
        </button>
      )}
    </Card>
  );
}
